import { useRef } from 'react';
import { LuPin } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, useSheetClose } from '@/components/ui/sheet';
import { useDockState } from 'views/dock/dock-state';

import { useSubmitTaskForm } from '../hooks/useSubmitTaskForm';
import { TaskForm, TaskFormRef, TaskFormType } from './TaskForm';

type CreateTaskSheetProps = {
    householdGUID: string;
    defaultFormValues: Partial<TaskFormType>;
    actionItemGUID?: string;
    noteGUID?: string;
    onClose: () => void;
};

export const CreateTaskSheet = (props: CreateTaskSheetProps) => (
    <Sheet defaultOpen onExitComplete={props.onClose}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" aria-describedby={undefined}>
            <CreateTaskSheetBody {...props} />
        </SheetContent>
    </Sheet>
);

const CreateTaskSheetBody = (props: CreateTaskSheetProps) => {
    const close = useSheetClose();
    const submitTaskForm = useSubmitTaskForm();
    const { addCreateTaskDockItem } = useDockState();
    const formRef = useRef<TaskFormRef>(null);

    // Move the in-progress form into the dock so the user can keep browsing; the dock item
    // carries the same household, action item and note links as this sheet.
    const handlePin = () => {
        addCreateTaskDockItem(
            props.householdGUID,
            formRef.current?.formMethods.getValues(),
            undefined,
            props.actionItemGUID,
            props.noteGUID,
        );
        close();
    };

    return (
        <>
            <SheetHeader>
                <SheetTitle>
                    <div className="flex items-center gap-2">
                        <span className="text-accent text-lg font-semibold">Create task</span>
                        <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Pin task"
                            className="shrink-0"
                            onClick={handlePin}
                        >
                            <LuPin className="h-4 w-4" />
                        </Button>
                    </div>
                </SheetTitle>
            </SheetHeader>
            <div className="pt-4">
                <TaskForm
                    ref={formRef}
                    householdMode="locked"
                    householdGUID={props.householdGUID}
                    defaultFormValues={{
                        ...props.defaultFormValues,
                        householdOption: { value: props.householdGUID, label: '' },
                    }}
                    actionItemGUID={props.actionItemGUID}
                    noteGUID={props.noteGUID}
                    onCancel={close}
                    onSubmit={async (data) => {
                        if (await submitTaskForm.submit(data)) close();
                    }}
                />
            </div>
        </>
    );
};
